import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useGame } from "@/context/GameContext";
import MyUniqueLooks from "@/components/MyUniqueLooks";
import { Link } from "react-router-dom";

function LookCard({ l, canBuy, busy, onBuy, reason, onSale }) {
  return (
    <div className="group rounded-2xl overflow-hidden border border-amber-400/40 bg-[#081820]/80 hover:border-amber-300 hover:-translate-y-1 transition-[transform,border-color] duration-300" data-testid={`unique-look-${l.id}`}>
      <div className="relative">
        <img src={`${process.env.REACT_APP_BACKEND_URL}${l.image}`} alt={l.name} loading="lazy" className="w-full aspect-square object-cover" />
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/75 text-[10px] font-mono-game text-amber-200 tracking-wider" data-testid={`unique-look-no-${l.id}`}>1/1 · #{l.no?.toLocaleString()}</span>
      </div>
      <div className="p-4">
        <p className="font-semibold text-sm text-white" data-testid={`unique-look-name-${l.id}`}>{l.name}</p>
        <p className="text-[11px] text-slate-400 mt-0.5 truncate">{l.traits.theme || `${l.traits.fur} fur · ${l.traits.headwear}`}</p>
        <button data-testid={`buy-unique-look-${l.id}`} disabled={!canBuy || busy} onClick={() => onBuy(l)}
          className="mt-3 w-full py-2 rounded-lg ember-btn text-xs disabled:opacity-40">{!onSale ? "COMING SOON" : busy ? "BUYING…" : `${l.price.toLocaleString()} credits`}</button>
        {reason && <p className="text-[10px] text-slate-500 mt-1.5 text-center" data-testid={`unique-look-why-${l.id}`}>{reason}</p>}
      </div>
    </div>
  );
}

export default function UniqueLooks() {
  const { game, refresh } = useGame();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const [mineKey, setMineKey] = useState(0);
  const [more, setMore] = useState(false);
  const sentinel = useRef(null);
  const load = (n = 24) => api.get("/looks/unique", { params: { limit: n } }).then(({ data }) => setD(data)).catch(() => {});
  const loadMore = () => {
    if (more || !d || d.looks.length >= d.total) return;
    setMore(true);
    api.get("/looks/unique", { params: { skip: d.looks.length, limit: 24 } })
      .then(({ data }) => setD((x) => ({ ...data, looks: [...x.looks, ...data.looks.filter((l) => !x.looks.some((o) => o.id === l.id))] })))
      .catch(() => {}).finally(() => setMore(false));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const el = sentinel.current;
    if (!el) return undefined;
    const ob = new IntersectionObserver((es) => es[0].isIntersecting && loadMore(), { rootMargin: "400px" });
    ob.observe(el);
    return () => ob.disconnect();
  });
  if (!d) return null;
  const buy = async (l) => {
    if (!window.confirm(`Buy "${l.name}" for ${l.price.toLocaleString()} credits? It's the only one that will ever exist.`)) return;
    setBusy(l.id);
    try { await api.post(`/looks/unique/${l.id}/buy`); toast.success(`${l.name} is yours, and your wolf is wearing it now`); await refresh(); setMineKey((k) => k + 1); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); load(Math.min(60, Math.max(24, d.looks.length))); }
  };
  return (
    <section className="mt-14" data-testid="unique-looks">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="font-display text-2xl text-amber-100 flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-300" />1/1 Wolf looks</h2>
          <p className="text-sm text-slate-400 mt-1">Every one is totally unique and can only be bought once, then claimed as a Solana NFT for a small SOL cost. Every approved wolf is on sale here.</p>
        </div>
        <Link to="/gallery" data-testid="unique-looks-gallery-link" className="text-xs text-cyan-300 hover:underline">See claimed wolves</Link>
        <p className="font-mono-game text-xs text-amber-200 border border-amber-400/40 rounded-full px-3 py-1" data-testid="unique-looks-counter">Wolf #{d.made.toLocaleString()} of {d.cap.toLocaleString()} made</p>
      </div>
      {d.complete ? <p className="text-amber-200 text-sm" data-testid="unique-looks-complete">Collection complete: all {d.cap.toLocaleString()} wolves have found owners.</p>
        : !d.looks.length ? <p className="text-slate-500 text-sm" data-testid="unique-looks-empty">New 1/1 wolves are on their way. Check back soon.</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-4">
          {d.looks.map((l) => <LookCard key={l.id} l={l} busy={busy === l.id} onSale={d.on_sale} canBuy={d.on_sale && !!game.wolf && game.user.credits >= l.price && !busy} onBuy={buy}
            reason={!d.on_sale ? `${l.price.toLocaleString()} credits when sales open` : !game.wolf ? "Create your wolf first" : game.user.credits < l.price ? `Need ${(l.price - game.user.credits).toLocaleString()} more credits` : null} />)}
        </div>
      )}
      {d.looks.length < d.total && (
        <div ref={sentinel} className="mt-6 text-center">
          <button data-testid="unique-looks-more" onClick={loadMore} disabled={more} className="text-xs text-cyan-300 border border-cyan-400/40 rounded-full px-4 py-2 hover:bg-cyan-500/10 disabled:opacity-60">
            {more ? "Loading…" : `Show more (${d.total - d.looks.length} left)`}
          </button>
        </div>
      )}
      <div className="mt-8"><MyUniqueLooks key={mineKey} title="Your 1/1 wolves" /></div>
    </section>
  );
}
